import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import Image from '../../components/Image';
import TextInput from '../../components/formComponents/inputs/TextInput';
import RichTextDisplay from '../../components/RichTextDisplay';
import CaptureClicksV2 from '../../../client/components/CaptureClicksV2';
import VerbButton, { VERB_BUTTON_TYPE } from '../../components/VerbButton';
import { checkForMediaLibraryLinkClicks } from '../../utils/FileUtils';

export default class ChooseOneSlideOptions extends PureComponent {
    static propTypes = {
        handleOptionSelect: PropTypes.func,
        onOtherOptionChange: PropTypes.func,

        completedData: PropTypes.object,
        includeChoiceLabels: PropTypes.bool,
        isImageType: PropTypes.bool,
        isWelcomeWorkspace: PropTypes.bool,
        isReflectionWorkspace: PropTypes.bool,
        options: PropTypes.arrayOf(PropTypes.object),
        selectChoice: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        displayType: PropTypes.string,

        currentUser: PropTypes.object,
        nextSectionName: PropTypes.string,
    };

    _isSelected = (option) => {
        const { selectChoice = '' } = this.props;
        return option.choice === (selectChoice || '').split('::::')[0];
    };

    _onOtherOptionChange = (choice) => (e) => {
        const { onOtherOptionChange } = this.props;
        if (!e?.target?.value) {
            const contents = Array.from(document.getElementsByClassName('playerContent'));

            const scrollToBottomAndFocus = () => {
                // have to do forEach since there might be multiple 'playerContent' elems on a page at one time
                contents.forEach((elem) =>
                    elem.scroll({
                        top: elem.scrollHeight, // scroll to the bottom of the element
                        behavior: 'smooth' // auto, smooth, initial, inherit
                    })
                );
            };

            if (contents.length) {
                // have to wait for option choice to change and render the text area before scrolling
                setTimeout(scrollToBottomAndFocus, 50);
            }
        }
        return onOtherOptionChange(e, choice);
    };

    _renderTextOptions = (options) => {
        const {
            includeChoiceLabels,
            handleOptionSelect,
            completedData = {},
            onOtherOptionChange,
            displayType = '',
            currentUser,
            nextSectionName,
        } = this.props;
        const { data } = completedData;
        const otherTextValue = data && data.split('::::').length && data.split('::::')[1];
        return options.map((option, index) => {
            const sanitizedData = option.label.replace(/<\/?p>/g, "");
            const isSelected = this._isSelected(option);
            const onClick = () => handleOptionSelect(option.choice, option.responseVariableChoice);
            const className = cns('chooseSlide-option', {
                selected: isSelected,
                'include-score': includeChoiceLabels
            });
            let style = { animation: `fadeIn ${0.25 * (index + 1)}s ease-in` };
            if (data) {
                style = {};
            }
            if (option.isOther && !option.disableOtherTextBox) {
                return (
                    <div key={index} className={'other-option-wrapper'}>
                        <div
                            className={className}
                            onClick={this._onOtherOptionChange(option.choice)}
                            key={index}
                            style={style}
                        >
                            <div className={'chooseSlide-optionRadio'} value={otherTextValue} tabIndex={'0'} />
                            <div className={'chooseSlide-other-option-label'}>{sanitizedData}</div>
                        </div>
                        {isSelected && (
                            <TextInput
                                ref={this.otherTextAreaRef}
                                componentClass={'textarea'}
                                rows={2}
                                className={'col-12 other-option-text-area'}
                                type={'text'}
                                value={otherTextValue}
                                placeholder={''}
                                onChange={(e) => {
                                    onOtherOptionChange(e, option.choice);
                                }}
                                autoFocus={true}
                            />
                        )}
                    </div>
                );
            }
            if (displayType === 'boxes') {
                return (
                    <div className={className} onClick={onClick} key={index} style={style}>
                        <RichTextDisplay
                            className={'chooseSlide-optionLabel'}
                            htmlToDisplay={sanitizedData}
                            currentUser={currentUser}
                            nextSectionName={nextSectionName}
                        />
                    </div>
                );
            }

            return (
                <div className={className} onClick={onClick} key={index} style={style}>
                    <div className={'chooseSlide-optionRadio'} onKeyPress={onClick} tabIndex={'0'} />
                    <RichTextDisplay
                        className={'chooseSlide-optionLabel'}
                        htmlToDisplay={sanitizedData}
                        currentUser={currentUser}
                        nextSectionName={nextSectionName}
                    />
                </div>
            );
        });
    };

    _renderBoxOptions = (options) => {
        const {
            includeChoiceLabels,
            handleOptionSelect,
            onOtherOptionChange,
            completedData = {},
            currentUser,
            nextSectionName,
        } = this.props;

        const { data } = completedData;
        const otherTextValue = data && data.split('::::').length && data.split('::::')[1];
        let otherOptionComponent = null;
        const boxOptions = options.map((option, index) => {
            const isSelected = this._isSelected(option);
            const onClick = () => handleOptionSelect(option.choice, option.responseVariableChoice);
            const className = cns('chooseSlide-option box-option', {
                selected: isSelected,
                'include-score': includeChoiceLabels
            });
            const style = { animation: `fadeIn ${0.25 * (index + 1)}s ease-in` };

            if (option.isOther && !option.disableOtherTextBox) {
                otherOptionComponent = (
                    <>
                        <div key={index}>
                            {isSelected && (
                                <TextInput
                                    componentClass={'textarea'}
                                    rows={2}
                                    className={'other-option-text-area other-option-text-area-box'}
                                    type={'text'}
                                    value={otherTextValue}
                                    placeholder={''}
                                    onChange={(e) => {
                                        onOtherOptionChange(e, option.choice);
                                    }}
                                />
                            )}
                        </div>
                    </>
                );
            }
            return (
                <div style={style} className={className} key={index} onClick={onClick}>
                    <RichTextDisplay
                        className={'chooseSlide-optionLabel'}
                        htmlToDisplay={option.label}
                        currentUser={currentUser}
                        nextSectionName={nextSectionName}
                    />
                </div>
            );
        });

        return (
            <>
                <div className={'box-options-container'}>{boxOptions}</div>
                <div>{otherOptionComponent}</div>
            </>
        );
    };

    _renderButtonOptions = (options) => {
        const { includeChoiceLabels, handleOptionSelect } = this.props;

        const buttonOptions = options.map((option, index) => {
            const isSelected = this._isSelected(option);
            const onClick = () => handleOptionSelect(option.choice, option.responseVariableChoice);
            const className = cns('chooseSlide-option button-option', {
                selected: isSelected,
                'include-score': includeChoiceLabels
            });
            const style = { animation: `fadeIn ${0.25 * (index + 1)}s ease-in` };

            return (
                <CaptureClicksV2
                    interruptingClickHandler={checkForMediaLibraryLinkClicks()}
                    key={`${option.label}-${index}`}
                >
                    <div style={style} className={className} key={index} onClick={onClick}>
                        <VerbButton
                            onClick={onClick}
                            textId={option.label}
                            defaultMessage={option.label}
                            buttonType={
                                index % 2 === 0 ? VERB_BUTTON_TYPE.PRIMARY_DEFAULT : VERB_BUTTON_TYPE.SECONDARY_DEFAULT
                            }
                        />
                    </div>
                </CaptureClicksV2>
            );
        });

        return <div className={'button-options-container'}>{buttonOptions}</div>;
    };

    _renderImageOptions = (options) => {
        const {
            includeChoiceLabels,
            handleOptionSelect,
            isWelcomeWorkspace,
            selectChoice,
            completedData = {},
            currentUser,
            nextSectionName,
        } = this.props;
        const { data } = completedData;
        const elementsToRender = [];
        let tempOptions = [];
        const imagesPerRow = isWelcomeWorkspace ? 4 : 3;
        options.forEach((option, index) => {
            const isSelected = this._isSelected(option);
            const onClick = () => handleOptionSelect(option.choice);

            const className = cns('chooseSlide-option image-option col-12 col-md-3', {
                unselected: selectChoice && selectChoice.length && !isSelected,
                selected: isSelected,
                'include-score': includeChoiceLabels
            });

            let style = { animation: `fadeIn ${0.25 * (index + 1)}s ease-in` };
            if (data) {
                style = {};
            }

            if (!option.isOther) {
                tempOptions.push(
                    <div className={className} key={index} onClick={onClick} onKeyPress={onClick} style={style}>
                        <div className={'chooseSlide-optionImage'} tabIndex={'0'}>
                            <Image src={option.image} dimension={{ h: 200, w: 200 }} alt={`Option ${index}`} />
                        </div>
                        <div className={'choose-radio-container'}>
                            <div className={'chooseSlide-optionRadio'} tabIndex={'0'} />
                        </div>
                        <RichTextDisplay
                            className={'chooseSlide-optionLabel'}
                            htmlToDisplay={option.label}
                            currentUser={currentUser}
                            nextSectionName={nextSectionName}
                        />
                    </div>
                );
            }
            if (tempOptions.length === imagesPerRow || index === options.length - 1) {
                elementsToRender.push(<div className={'choose-image-row row'}>{tempOptions}</div>);
                tempOptions = [];
            }
        });

        return elementsToRender;
    };

    render() {
        const { isImageType, options, displayType = '', isReflectionWorkspace = false } = this.props;

        if (displayType === 'pill') {
            return this._renderBoxOptions(options);
        }
        if (isImageType) {
            return this._renderImageOptions(options);
        }
        if (displayType === 'buttons' && isReflectionWorkspace) {
            return this._renderButtonOptions(options);
        }
        return this._renderTextOptions(options);
    }
}
